"use client";

import type * as React from "react";
import { useState } from "react";
import { Arcade } from "@/components/ui/atoms/icons";
import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
} from "@/components/ui/atoms/popover";
import {
  RemovableChip,
  RemovableChipButton,
} from "@/components/ui/atoms/removable-chip";
import { ScrollArea } from "@/components/ui/atoms/scroll-area";
import { cn } from "@/lib/utils";
import { McpToolkitAvatar } from "./toolkit-avatar";

export type McpRecommendedMcpServer = {
  /** Stable React key — typically the MCP server name or id. */
  id: string;
  /** MCP server display name. */
  name: string;
  /**
   * Branded-icon lookup key passed through to `McpToolkitAvatar` — names the
   * key in the icon registry, matching the avatar's own prop.
   */
  toolkitId?: string;
  /** Total tools selected from this MCP server. */
  selectedCount?: number;
  /** Total tools the MCP server exposes (for the partial fraction). */
  total?: number;
};

export type McpRecommendedToolkitsChipProps = {
  /**
   * The recommended MCP servers grouped into this chip. The chip renders a
   * fixed "Recommended tools" label with the count; the popover scrolls
   * through the full list.
   */
  mcpServers: McpRecommendedMcpServer[];
  /**
   * The grouped review pane is currently filtered to recommended. Adds an
   * external ring so the active state reads at a glance.
   */
  active?: boolean;
  /**
   * Open the grouped selected-tools view, scoped to the recommended set.
   * Triggered by clicking the chip body (outside the trailing ×).
   */
  onReview?: () => void;
  /**
   * Open this MCP server's tools to adjust the selection. Triggered from the
   * popover list rows.
   */
  onOpenMcpServer?: (id: string) => void;
  /** Remove all recommended MCP servers at once. */
  onRemove?: () => void;
} & Omit<React.ComponentProps<"div">, "children" | "onRemove">;

/**
 * Branded Arcade badge — a small navy rounded square with the Arcade "A" mark,
 * used to identify this chip (and its popover header) as Arcade-authored.
 */
function ArcadeBadge({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-flex size-4 shrink-0 items-center justify-center rounded-[4px] bg-brand-navy text-white",
        className
      )}
      data-slot="mcp-recommended-toolkits-chip-badge"
    >
      <Arcade className="size-2.5" />
    </span>
  );
}

/**
 * Single grouped chip that stands in for the per-server "Recommended" chips
 * in the selection summary bar. The body opens the grouped review pane
 * filtered to recommended; the trailing × removes the whole recommended set
 * in one click; clicking the count opens a popover scrolling through every
 * recommended server so the user can drill into one without leaving the bar.
 *
 * The chip is branded with Arcade's "A" badge so it reads as "curated by
 * Arcade" at a glance — visually distinct from per-server toolkit chips.
 */
export function McpRecommendedToolkitsChip({
  mcpServers,
  active = false,
  onReview,
  onOpenMcpServer,
  onRemove,
  className,
  ...rest
}: McpRecommendedToolkitsChipProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const count = mcpServers.length;
  const serverWord = count === 1 ? "server" : "servers";
  const reviewLabel = `Review ${count} recommended ${serverWord}`;
  const popoverLabel = `Show ${count} recommended ${serverWord}`;
  const removeLabel = `Remove all ${count} recommended ${serverWord}`;

  return (
    <RemovableChip
      {...rest}
      active={active}
      className={cn(
        active ? "bg-brand-navy/15 ring-brand-navy/40" : null,
        className
      )}
      data-slot="mcp-recommended-toolkits-chip"
      data-status={active ? "open" : "closed"}
      onRemove={onRemove}
      removeLabel={removeLabel}
    >
      <RemovableChipButton
        aria-label={reviewLabel}
        className="pr-1.5 font-medium"
        onClick={onReview}
        title={reviewLabel}
      >
        <ArcadeBadge />
        <span>Recommended tools</span>
      </RemovableChipButton>
      <Popover onOpenChange={setPopoverOpen} open={popoverOpen}>
        <PopoverTrigger
          aria-label={popoverLabel}
          className="inline-flex cursor-pointer items-center gap-1 py-1 pr-1.5 pl-1 text-muted-foreground transition-[background-color,color] duration-[var(--duration-fast)] ease-[var(--ease-smooth)] hover:bg-foreground/10 hover:text-foreground focus-visible:bg-foreground/10 focus-visible:outline-none motion-reduce:transition-colors"
          title={popoverLabel}
          type="button"
        >
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-foreground tabular-nums">
            {count}
          </span>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 gap-0 p-0" sideOffset={6}>
          <PopoverHeader className="flex items-center gap-2 px-3 pt-2.5 pb-1.5 font-medium text-foreground text-xs">
            <ArcadeBadge />
            <span>Recommended by Arcade</span>
          </PopoverHeader>
          <ScrollArea className="max-h-72" orientation="vertical" type="hover">
            <ul className="flex flex-col py-1">
              {mcpServers.map(
                ({ id, name, toolkitId, selectedCount, total }) => (
                  <li key={id}>
                    <button
                      aria-label={`Open ${name} tools`}
                      className="flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors hover:bg-foreground/[0.06] focus-visible:bg-foreground/[0.06] focus-visible:outline-none"
                      onClick={() => {
                        setPopoverOpen(false);
                        onOpenMcpServer?.(id);
                      }}
                      type="button"
                    >
                      <McpToolkitAvatar
                        name={name}
                        size="sm"
                        toolkitId={toolkitId}
                      />
                      <span className="min-w-0 flex-1 truncate font-medium capitalize">
                        {name}
                      </span>
                      {selectedCount != null && total != null ? (
                        <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums">
                          {selectedCount === total
                            ? `${total}`
                            : `${selectedCount}/${total}`}
                        </span>
                      ) : null}
                    </button>
                  </li>
                )
              )}
            </ul>
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </RemovableChip>
  );
}
